import { For, Show } from "solid-js";
import { Link } from "@/lib/u-router";
import { Users } from "lucide-solid";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyShoppingIllustration } from "@/components/ui/illustrations";
import { useShoppingListsQuery } from "@mealplan/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

export function ShoppingListsView() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const lists = useShoppingListsQuery({
    db,
    drizzle,
    userId: session.userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  return (
    <div class="space-y-6">
      <h2 class="font-serif text-2xl font-semibold tracking-tight">{t(msg`Shopping lists`)}</h2>
      <Show
        when={(lists.data ?? []).length > 0}
        fallback={
          <Show when={!lists.isLoading}>
            <div class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-12 text-center">
              <EmptyShoppingIllustration class="mb-4 h-28 w-36 text-muted-foreground" />
              <h3 class="text-lg font-semibold">{t(msg`No shopping lists`)}</h3>
              <p class="text-sm text-muted-foreground">
                {t(msg`Generate one from your weekly plan.`)}
              </p>
            </div>
          </Show>
        }
      >
        <div class="space-y-3">
          <For each={lists.data}>
            {(list) => (
              <Link to="/shopping/$listId" params={{ listId: list.id }}>
                <Card class="transition-colors hover:bg-accent/5" data-testid="shopping-list-card">
                  <CardContent class="flex items-center justify-between gap-3 p-4">
                    <div class="min-w-0">
                      <p class="truncate font-medium">{list.name}</p>
                      <Show when={list.week_start}>
                        {(week) => (
                          <p class="text-xs text-muted-foreground">
                            {t(msg`Week of {date}`, { date: week() })}
                          </p>
                        )}
                      </Show>
                    </div>
                    <Show when={list.user_id !== session.userId()}>
                      <Badge variant="outline" class="gap-1">
                        <Users class="h-3 w-3" />
                        {t(msg`Shared`)}
                      </Badge>
                    </Show>
                  </CardContent>
                </Card>
              </Link>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
